/**
 * Adds the "Publish Recital" button (sidebar + discussion dropdown) for
 * coordinators viewing submission threads, and the "Add to Recital" action
 * in post dropdowns for promoting late submissions into reserved slots.
 *
 * Visibility rules:
 * - Publish button: only on submission threads, only for users with recital.publish
 * - Add to Recital: only on published recitals (no submission tag), only for
 *   non-performance comment posts (skips index, performances, and event posts)
 */
import { extend } from 'flarum/common/extend';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import PostControls from 'flarum/forum/utils/PostControls';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import Button from 'flarum/common/components/Button';
import PublishRecitalModal from './components/PublishRecitalModal';
import AddToRecitalModal from './components/AddToRecitalModal';
import { isSubmissionThread, getSubmissionTagIds } from './recitalTypes';
export default function addPublishButton() {
  // Add to the dropdown menu
  extend(DiscussionControls, 'moderationControls', function (items, discussion) {
    if (!app.forum.attribute('canPublishRecital')) return;
    if (!isSubmissionThread(discussion)) return;
    items.add('publishRecital', Button.component({
      icon: 'fas fa-music',
      onclick: () => app.modal.show(PublishRecitalModal, {
        discussion
      })
    }, app.translator.trans('pianotell-recitals.forum.publish_button')), 100);
  });

  // Add a prominent standalone button in the discussion sidebar
  extend(DiscussionPage.prototype, 'sidebarItems', function (items) {
    if (!app.forum.attribute('canPublishRecital')) return;
    const discussion = this.discussion;
    if (!discussion) return;
    if (!isSubmissionThread(discussion)) return;
    items.add('publishRecital', Button.component({
      icon: 'fas fa-music',
      className: 'Button Button--recitalPublish Button--block',
      onclick: () => app.modal.show(PublishRecitalModal, {
        discussion
      })
    }, app.translator.trans('pianotell-recitals.forum.publish_button')), 50);
  });

  // "Add to Recital" in post dropdown for coordinators on published recitals
  extend(PostControls, 'moderationControls', function (items, post) {
    if (!app.forum.attribute('canPublishRecital')) return;
    if (!post || !post.discussion()) return;
    var discussion = post.discussion();
    var tags = discussion.tags();
    if (!tags) return;
    var recitalTagId = app.forum.attribute('recitalsRecitalTagId');
    var submissionTagIds = getSubmissionTagIds();
    var hasRecitalTag = recitalTagId && tags.some(function (t) {
      return t && String(t.id()) === String(recitalTagId);
    });
    var hasSubmissionTag = submissionTagIds.length > 0 && tags.some(function (t) {
      return t && submissionTagIds.indexOf(String(t.id())) !== -1;
    });

    // Only on published recitals, non-performance posts, not post 1
    if (!hasRecitalTag || hasSubmissionTag) return;
    if (post.number() <= 1) return;

    // Only show if the discussion has reserved slots available
    var posts = discussion.posts();
    var hasReservedSlots = posts && posts.some(function (p) {
      return p && p.contentType && p.contentType() === 'reservedSlot';
    });
    if (!hasReservedSlots) return;

    // Skip non-comment posts (e.g., reservedSlot events)
    if (post.contentType() !== 'comment') return;

    // Skip posts whose author is already a performer in the recital.
    // Performers are comment post authors between post 1 and the first reserved slot.
    if (posts) {
      var firstSlotNumber = Infinity;
      posts.forEach(function (p) {
        if (p && p.contentType && p.contentType() === 'reservedSlot' && p.number() < firstSlotNumber) {
          firstSlotNumber = p.number();
        }
      });
      var performerUserIds = [];
      posts.forEach(function (p) {
        if (p && p.contentType && p.contentType() === 'comment' && p.number() > 1 && p.number() < firstSlotNumber && p.user() && !p.isHidden()) {
          performerUserIds.push(String(p.user().id()));
        }
      });
      var postUser = post.user();
      if (postUser && performerUserIds.indexOf(String(postUser.id())) !== -1) return;
    }
    items.add('addToRecital', Button.component({
      icon: 'fas fa-music',
      onclick: () => app.modal.show(AddToRecitalModal, {
        post
      })
    }, 'Add to Recital'));
  });
}